import React, { useState, useCallback } from 'react';
import {
    Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, Checkbox, Box,
    Select, MenuItem, ListSubheader, Button, Chip, CircularProgress
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../services/api';
import { TAX_CATEGORIES, TAX_CAT_MAP, TAX_GROUPS } from '../constants/taxCategories';
import { PERSONAL_CATEGORIES, PERSONAL_CAT_MAP, PERSONAL_GROUPS } from '../constants/personalCategories';

function CategorySelect({ categories, catMap, groups, value, onChange, emptyLabel, accentColor }) {
    return (
        <Select size="small" displayEmpty value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            sx={{
                minWidth: 180, fontSize: '0.8rem',
                backgroundColor: value ? '#e8f5e9' : '#fff3e0',
                '& .MuiSelect-select': { py: 0.5 }
            }}>
            <MenuItem value="" sx={{ fontSize: '0.8rem', color: '#999' }}>{emptyLabel || 'Uncategorized'}</MenuItem>
            {groups.map(group => [
                <ListSubheader key={group} sx={{ fontSize: '0.75rem', fontWeight: 700, color: accentColor || '#667eea', lineHeight: '28px' }}>
                    {group}
                </ListSubheader>,
                ...categories.filter(c => c.group === group).map(c => (
                    <MenuItem key={c.code} value={c.code} sx={{ fontSize: '0.8rem', pl: 3 }}>
                        {c.code.toUpperCase()} – {c.label}
                    </MenuItem>
                ))
            ])}
        </Select>
    );
}

function BulkActionBar({ selectedCount, onApply, onClear }) {
    const [bulkCat, setBulkCat] = useState('');
    const [bulkType, setBulkType] = useState('');
    const catList = bulkType === 'Personal' ? PERSONAL_CATEGORIES : TAX_CATEGORIES;
    const catGroups = bulkType === 'Personal' ? PERSONAL_GROUPS : TAX_GROUPS;
    const catMap = bulkType === 'Personal' ? PERSONAL_CAT_MAP : TAX_CAT_MAP;

    const handleApply = () => {
        onApply({
            type: bulkType || null,
            taxCategory: bulkType !== 'Personal' ? (bulkCat || null) : null,
            personalCategory: bulkType === 'Personal' ? (bulkCat || null) : null,
        });
        setBulkCat('');
        setBulkType('');
    };

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1,
            borderRadius: 2, backgroundColor: '#e3f2fd', border: '1px solid #90caf9',
            flexWrap: 'wrap'
        }}>
            <Chip label={`${selectedCount} selected`} color="primary" size="small" sx={{ fontWeight: 700 }} />
            <Select size="small" displayEmpty value={bulkType}
                onChange={(e) => { setBulkType(e.target.value); setBulkCat(''); }}
                sx={{ minWidth: 130, fontSize: '0.85rem', backgroundColor: '#fff' }}>
                <MenuItem value=""><em>Keep Type</em></MenuItem>
                <MenuItem value="Personal">Personal</MenuItem>
                <MenuItem value="Business">Business</MenuItem>
            </Select>
            {bulkType && (
                <Select size="small" displayEmpty value={bulkCat}
                    onChange={(e) => setBulkCat(e.target.value)}
                    sx={{ minWidth: 220, fontSize: '0.85rem', backgroundColor: '#fff' }}
                    renderValue={(val) => {
                        if (!val) return <em>Set Category...</em>;
                        const c = catMap[val];
                        return c ? `${c.code.toUpperCase()} – ${c.label}` : val;
                    }}>
                    <MenuItem value=""><em>No change</em></MenuItem>
                    {catGroups.map(group => [
                        <ListSubheader key={group} sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#667eea', lineHeight: '28px' }}>
                            {group}
                        </ListSubheader>,
                        ...catList.filter(c => c.group === group).map(c => (
                            <MenuItem key={c.code} value={c.code} sx={{ fontSize: '0.85rem', pl: 3 }}>
                                {c.code.toUpperCase()} – {c.label}
                            </MenuItem>
                        ))
                    ])}
                </Select>
            )}
            <Button variant="contained" size="small" onClick={handleApply}
                disabled={!bulkType && !bulkCat}
                sx={{ textTransform: 'none', fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' } }}>
                Apply
            </Button>
            <Button variant="text" size="small" onClick={onClear}
                sx={{ textTransform: 'none', color: '#666' }}>
                Clear
            </Button>
        </Box>
    );
}

function TransactionGroup({
    transactions, hiddenTransactions, onHideAllChange, onCheckboxChange, vendor, expanded,
    onAccordionChange, onSaveTransactionType, onSaveTaxCategory, onSavePersonalCategory, onTransactionsUpdated
}) {
    const [selected, setSelected] = useState(new Set());
    const [aiLoading, setAiLoading] = useState(false);

    const visibleTransactions = transactions.filter(transaction => !hiddenTransactions.has(transaction.id));
    const totalAmount = visibleTransactions.reduce((sum, transaction) => sum + Math.abs(transaction.amount), 0).toFixed(2);
    const allHidden = transactions.every(transaction => hiddenTransactions.has(transaction.id));
    const allSelected = transactions.length > 0 && transactions.every(tx => selected.has(tx.id));

    const handleHideAllClick = (e) => {
        e.stopPropagation();
        onHideAllChange(transactions, e.target.checked);
    };

    const toggleSelect = useCallback((id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelected(prev => {
            if (transactions.every(tx => prev.has(tx.id))) return new Set();
            return new Set(transactions.map(tx => tx.id));
        });
    }, [transactions]);

    const handleBulkApply = useCallback(({ type, taxCategory, personalCategory }) => {
        const ids = [...selected];
        ids.forEach(id => {
            if (type && onSaveTransactionType) onSaveTransactionType(id, type);
            if (taxCategory && onSaveTaxCategory) onSaveTaxCategory(id, taxCategory);
            if (personalCategory && onSavePersonalCategory) onSavePersonalCategory(id, personalCategory);
        });
        setSelected(new Set());
    }, [selected, onSaveTransactionType, onSaveTaxCategory, onSavePersonalCategory]);

    const handleAutoCategorize = useCallback(async () => {
        const ids = selected.size > 0 ? [...selected] : transactions.map(tx => tx.id);
        const payload = ids.map(id => {
            const tx = transactions.find(t => t.id === id);
            return { id, type: tx?.classification || tx?.tag?.type || 'Personal' };
        });
        setAiLoading(true);
        try {
            const res = await api.post('/api/csv/auto-categorize', payload);
            if (onTransactionsUpdated) onTransactionsUpdated(res.data.results);
            setSelected(new Set());
        } catch (e) {
            alert('Auto-categorize failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setAiLoading(false);
        }
    }, [selected, transactions, onTransactionsUpdated]);

    return (
        <Accordion expanded={expanded} onChange={onAccordionChange}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" width="100%">
                    <Checkbox onChange={handleHideAllClick} checked={allHidden}
                        style={{ marginRight: '10px' }} onClick={(e) => e.stopPropagation()} />
                    <Typography variant="subtitle1" flexGrow={1}>
                        {vendor} (Total: ${totalAmount})
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {selected.size > 0 && (
                        <BulkActionBar selectedCount={selected.size}
                            onApply={handleBulkApply} onClear={() => setSelected(new Set())} />
                    )}
                    <Button variant="outlined" size="small" onClick={handleAutoCategorize}
                        disabled={aiLoading}
                        startIcon={aiLoading ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#667eea', color: '#667eea',
                            '&:hover': { borderColor: '#764ba2', color: '#764ba2', backgroundColor: 'rgba(102,126,234,0.04)' } }}>
                        {aiLoading ? 'Categorizing...' : `AI Categorize${selected.size > 0 ? ` (${selected.size})` : ' All'}`}
                    </Button>
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox checked={allSelected} indeterminate={selected.size > 0 && !allSelected}
                                        onChange={toggleSelectAll} size="small" />
                                </TableCell>
                                <TableCell><strong>Hide</strong></TableCell>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell><strong>Amount</strong></TableCell>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Type</strong></TableCell>
                                <TableCell><strong>Category</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.map(transaction => {
                                const txType = transaction.classification || transaction.tag?.type || "Select";
                                const isBusiness = txType === 'Business';
                                const isPersonal = txType === 'Personal';
                                const taxCode = transaction.taxCategory || '';
                                const persCode = transaction.personalCategory || '';
                                const isSelected = selected.has(transaction.id);
                                return (
                                <TableRow key={transaction.id} sx={{
                                    backgroundColor: isSelected ? 'rgba(102,126,234,0.08)' :
                                        isBusiness && !taxCode ? 'rgba(255,243,224,0.4)' : 'inherit'
                                }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={isSelected} onChange={() => toggleSelect(transaction.id)} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox checked={hiddenTransactions.has(transaction.id)}
                                            onChange={() => onCheckboxChange(transaction.id)} size="small" />
                                    </TableCell>
                                    <TableCell>{transaction.id}</TableCell>
                                    <TableCell>{transaction.description}</TableCell>
                                    <TableCell>{Math.abs(transaction.amount).toFixed(2)}</TableCell>
                                    <TableCell>{transaction.date}</TableCell>
                                    <TableCell>
                                        {onSaveTransactionType ? (
                                            <Select value={txType}
                                                onChange={(e) => onSaveTransactionType(transaction.id, e.target.value)}
                                                size="small" sx={{ minWidth: 120 }}>
                                                <MenuItem value="Select" disabled>Select</MenuItem>
                                                <MenuItem value="Personal">Personal</MenuItem>
                                                <MenuItem value="Business">Business</MenuItem>
                                            </Select>
                                        ) : txType}
                                    </TableCell>
                                    <TableCell>
                                        {isBusiness && onSaveTaxCategory ? (
                                            <CategorySelect categories={TAX_CATEGORIES} catMap={TAX_CAT_MAP}
                                                groups={TAX_GROUPS} value={taxCode}
                                                onChange={(val) => onSaveTaxCategory(transaction.id, val)}
                                                emptyLabel="Uncategorized" accentColor="#667eea" />
                                        ) : isPersonal && onSavePersonalCategory ? (
                                            <CategorySelect categories={PERSONAL_CATEGORIES} catMap={PERSONAL_CAT_MAP}
                                                groups={PERSONAL_GROUPS} value={persCode}
                                                onChange={(val) => onSavePersonalCategory(transaction.id, val)}
                                                emptyLabel="Uncategorized" accentColor="#e65100" />
                                        ) : (
                                            <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>—</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )})}
                        </TableBody>
                    </Table>
                </TableContainer>
            </AccordionDetails>
        </Accordion>
    );
}

export default TransactionGroup;
